import { useState, useEffect } from 'react'
import { X, Tag, DollarSign, Trash2, Package } from 'lucide-react'

interface BulkListedSlideoutProps {
  isOpen: boolean
  onClose: () => void
  itemIds: string[]
  onSuccess?: () => void
}

interface ItemListing {
  itemId: string
  itemIc: string
  itemName: string
  existingPlatforms: string[]
}

export default function BulkListedSlideout({ isOpen, onClose, itemIds, onSuccess }: BulkListedSlideoutProps) {
  const [itemsData, setItemsData] = useState<ItemListing[]>([])
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([])
  const [price, setPrice] = useState('')
  const [status, setStatus] = useState('active')
  const [action, setAction] = useState<'add' | 'remove' | 'toggle'>('add')
  const [loading, setLoading] = useState(false)
  const [fetchingData, setFetchingData] = useState(false)
  const [error, setError] = useState('')

  const platforms = ['eBay', 'Mercari', 'Poshmark', 'Facebook Marketplace', 'Amazon', 'Etsy', 'Shopify', 'Other']

  // Fetch items and their existing listings when slideout opens
  useEffect(() => {
    if (isOpen && itemIds.length > 0) {
      fetchItemsData()
    } else {
      // Reset form when closed
      setSelectedPlatforms([])
      setPrice('')
      setStatus('active')
      setAction('add')
      setError('')
      setItemsData([])
    }
  }, [isOpen, itemIds])

  const fetchItemsData = async () => {
    setFetchingData(true)
    try {
      // Fetch all items and their listings
      const promises = itemIds.map(async (itemId) => {
        // Get item details
        const itemResponse = await fetch(`/api/items-pg/${itemId}`)
        const itemData = await itemResponse.json()

        // Get existing listings
        const listingsResponse = await fetch(`/api/listings-pg?item_id=${itemId}`)
        const listingsData = await listingsResponse.json()

        return {
          itemId,
          itemIc: itemData.data?.ic || 'Unknown',
          itemName: itemData.data?.name || 'Unknown',
          existingPlatforms: listingsData.success ? listingsData.listings.map((l: any) => l.platform) : []
        }
      })

      const results = await Promise.all(promises)
      setItemsData(results)
    } catch (err) {
      console.error('Error fetching items data:', err)
      setError('Failed to load items data')
    } finally {
      setFetchingData(false)
    }
  }

  const togglePlatform = (platform: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (selectedPlatforms.length === 0) {
      setError('Please select at least one marketplace')
      return
    }

    setLoading(true)
    setError('')

    try {
      if (action === 'add') {
        // Add listings to all selected items
        const promises: Promise<any>[] = []

        itemIds.forEach(itemId => {
          selectedPlatforms.forEach(platform => {
            promises.push(
              fetch('/api/listings-pg', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  item_id: itemId,
                  platform,
                  price: price ? parseFloat(price) : null,
                  status
                })
              })
            )
          })
        })

        const responses = await Promise.all(promises)

        // Check if all succeeded
        for (const response of responses) {
          if (!response.ok) {
            const data = await response.json()
            throw new Error(data.error || 'Failed to add listings')
          }
        }

        if (onSuccess) onSuccess()
        onClose()

      } else if (action === 'remove') {
        // Remove selected platforms from all items
        const promises: Promise<any>[] = []

        // First, fetch all existing listings for selected items and platforms
        for (const itemId of itemIds) {
          const response = await fetch(`/api/listings-pg?item_id=${itemId}`)
          const data = await response.json()

          if (data.success && data.listings) {
            const listingsToDelete = data.listings.filter((l: any) =>
              selectedPlatforms.includes(l.platform)
            )

            listingsToDelete.forEach((listing: any) => {
              promises.push(
                fetch(`/api/listings-pg?id=${listing.id}`, {
                  method: 'DELETE'
                })
              )
            })
          }
        }

        await Promise.all(promises)

        if (onSuccess) onSuccess()
        onClose()

      } else if (action === 'toggle') {
        // Toggle status for selected platforms on all items
        const promises: Promise<any>[] = []

        for (const itemId of itemIds) {
          const response = await fetch(`/api/listings-pg?item_id=${itemId}`)
          const data = await response.json()

          if (data.success && data.listings) {
            const listingsToToggle = data.listings.filter((l: any) =>
              selectedPlatforms.includes(l.platform)
            )

            listingsToToggle.forEach((listing: any) => {
              const newStatus = listing.status === 'active' ? 'ended' : 'active'
              promises.push(
                fetch('/api/listings-pg', {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    id: listing.id,
                    listing_id: listing.listing_id,
                    title: listing.title,
                    price: listing.price,
                    status: newStatus
                  })
                })
              )
            })
          }
        }

        await Promise.all(promises)

        if (onSuccess) onSuccess()
        onClose()
      }

    } catch (err: any) {
      setError(err.message || 'Operation failed')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  // Calculate platform availability across all items
  const platformCounts = platforms.map(platform => ({
    platform,
    count: itemsData.filter(item => item.existingPlatforms.includes(platform)).length
  }))

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9998,
          backdropFilter: 'blur(4px)'
        }}
        onClick={onClose}
      />

      {/* Slideout */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '600px',
          maxWidth: '90vw',
          background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
          boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          borderLeft: '1px solid rgba(59, 130, 246, 0.3)'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
            background: 'rgba(59, 130, 246, 0.05)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.3)'
                }}
              >
                <Package style={{ width: 20, height: 20, color: '#3b82f6' }} />
              </div>
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#93c5fd', margin: 0 }}>
                  Bulk Marketplace Listings
                </h2>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '4px 0 0 0' }}>
                  {itemIds.length} items selected
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <X style={{ width: 24, height: 24 }} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            padding: '24px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}
        >
          {error && (
            <div
              style={{
                padding: '12px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '6px',
                color: '#ef4444',
                fontSize: '0.875rem'
              }}
            >
              {error}
            </div>
          )}

          {fetchingData ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
              Loading items data...
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Action Type */}
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '12px',
                    color: '#93c5fd',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}
                >
                  Action
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[
                    { value: 'add', label: 'Add Listings', color: '#22C55E' },
                    { value: 'toggle', label: 'Toggle Status', color: '#EAB308' },
                    { value: 'remove', label: 'Remove Listings', color: '#EF4444' }
                  ].map(({ value, label, color }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setAction(value as any)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        background: action === value ? `${color}20` : 'rgba(31, 41, 55, 0.5)',
                        border: action === value ? `2px solid ${color}` : '1px solid rgba(59, 130, 246, 0.3)',
                        borderRadius: '6px',
                        color: action === value ? color : '#9ca3af',
                        fontSize: '0.875rem',
                        fontWeight: action === value ? '600' : '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Marketplaces - Multi Select with counts */}
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '12px',
                    color: '#93c5fd',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}
                >
                  Select Marketplaces
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {platformCounts.map(({ platform, count }) => (
                    <button
                      key={platform}
                      type="button"
                      onClick={() => togglePlatform(platform)}
                      style={{
                        padding: '8px 16px',
                        background: selectedPlatforms.includes(platform)
                          ? 'rgba(59, 130, 246, 0.2)'
                          : 'rgba(31, 41, 55, 0.5)',
                        border: selectedPlatforms.includes(platform)
                          ? '2px solid #3b82f6'
                          : '1px solid rgba(59, 130, 246, 0.3)',
                        borderRadius: '6px',
                        color: selectedPlatforms.includes(platform) ? '#93c5fd' : '#9ca3af',
                        fontSize: '0.875rem',
                        fontWeight: selectedPlatforms.includes(platform) ? '600' : '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      {platform}
                      {count > 0 && (
                        <span style={{
                          fontSize: '0.7rem',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          background: 'rgba(34, 211, 238, 0.3)',
                          color: '#22D3EE'
                        }}>
                          {count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                {selectedPlatforms.length > 0 && (
                  <p style={{ marginTop: '8px', fontSize: '0.75rem', color: '#6b7280' }}>
                    Selected: {selectedPlatforms.join(', ')}
                  </p>
                )}
              </div>

              {/* Price - Only for Add action */}
              {action === 'add' && (
                <div>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '8px',
                      color: '#93c5fd',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}
                  >
                    Listed Price (Optional - applies to all)
                  </label>
                  <div style={{ position: 'relative' }}>
                    <DollarSign
                      style={{
                        position: 'absolute',
                        left: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 16,
                        height: 16,
                        color: '#6b7280'
                      }}
                    />
                    <input
                      type="number"
                      step="0.01"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="0.00"
                      style={{
                        width: '100%',
                        padding: '10px 10px 10px 36px',
                        background: '#1f2937',
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        borderRadius: '6px',
                        color: '#e5e7eb',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Status - Only for Add action */}
              {action === 'add' && (
                <div>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '8px',
                      color: '#93c5fd',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}
                  >
                    Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: '#1f2937',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      color: '#e5e7eb',
                      fontSize: '0.875rem'
                    }}
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="ended">Ended</option>
                  </select>
                </div>
              )}

              {/* Items Preview */}
              <div>
                <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#93c5fd', marginBottom: '12px', textTransform: 'uppercase' }}>
                  Selected Items ({itemsData.length})
                </h3>
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  background: 'rgba(31, 41, 55, 0.3)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '6px',
                  padding: '12px'
                }}>
                  {itemsData.map((item) => (
                    <div
                      key={item.itemId}
                      style={{
                        padding: '8px',
                        marginBottom: '6px',
                        background: 'rgba(59, 130, 246, 0.05)',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        color: '#e5e7eb'
                      }}
                    >
                      <div style={{ fontWeight: '600', color: '#93c5fd' }}>{item.itemIc}</div>
                      <div style={{ color: '#9ca3af', marginTop: '2px' }}>{item.itemName}</div>
                      {item.existingPlatforms.length > 0 && (
                        <div style={{ marginTop: '4px', fontSize: '0.7rem', color: '#6b7280' }}>
                          Listed: {item.existingPlatforms.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div style={{
                padding: '12px',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: '#93c5fd'
              }}>
                <strong>Summary:</strong> {action === 'add' ? 'Add' : action === 'toggle' ? 'Toggle status for' : 'Remove'} {selectedPlatforms.length} marketplace{selectedPlatforms.length !== 1 ? 's' : ''} {action === 'add' ? 'to' : action === 'toggle' ? 'on' : 'from'} {itemIds.length} item{itemIds.length !== 1 ? 's' : ''}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px', paddingTop: '12px' }}>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'transparent',
                    border: '1px solid rgba(156, 163, 175, 0.3)',
                    borderRadius: '6px',
                    color: '#9ca3af',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.5 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || selectedPlatforms.length === 0}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: action === 'add' ? 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' :
                               action === 'toggle' ? 'linear-gradient(135deg, #EAB308 0%, #CA8A04 100%)' :
                               'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: (loading || selectedPlatforms.length === 0) ? 'not-allowed' : 'pointer',
                    opacity: (loading || selectedPlatforms.length === 0) ? 0.5 : 1
                  }}
                >
                  {loading ? 'Processing...' :
                   action === 'add' ? 'Add Listings' :
                   action === 'toggle' ? 'Toggle Status' :
                   'Remove Listings'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  )
}
